import _compact from 'lodash/compact';
import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createDraftSafeSelector,
  current,
  createAction,
} from '@reduxjs/toolkit';
import { isNonFundraiserProduct } from 'redux/helpers';
import {
  rejectValue,
  flatCartProduct,
  buildFromCartMeta,
  flatCalculateData,
  flatCalculateMeta,
} from 'redux/helpers';
import { priceFromAPI } from 'utils/price';
import {
  GET_CART,
  POST_CART,
  PATCH_CART_PRODUCT,
  DELETE_CART_PRODUCT,
  CLEAN_CART,
  POST_CALCULATE_FEES,
} from 'services/api';
import {
  CART,
  CART_ALL_ACTION,
  CART_UPDATE_ACTION,
  CART_DELETE_ACTION,
  CART_ADD_ACTION,
  CART_CLEAN_ACTION,
  CART_CALCULATE_FEES_ACTION,
  SET_TOTAL_CHARGE,
} from './types';
import { REGULAR_PRODUCT, FUNDRAISER_PRODUCT } from 'services/api/types';
import { logEvent } from 'utils/ga';
import { CURRENCY_SYMBOL } from 'utils/transform';

const cartAdapter = createEntityAdapter();

export const setTotalCharge = createAction(SET_TOTAL_CHARGE);

export const initialState = cartAdapter.getInitialState({
  loading: false,
  subtotal: priceFromAPI(0),
  discountApplied: priceFromAPI(0),
  total: priceFromAPI(0),
  shippingCost: priceFromAPI(0),
  tax: priceFromAPI(0),
  currencySymbol: '€',
  isUpdating: false,
  userPaysFees: false,
  fees: priceFromAPI(0),
  comments: '',
  editingProduct: null,
  calculatingFees: null,
  isAnonymous: null,
  containsNonFundraiserProducts: false,
  shippingMethod: null,
  stripeTotal: 0,
  stripeData: null,
});

export const setStripeTotalCharge = createAction("SET_STRIPE_TOTAL");
export const setStripeData = createAction('SET_STRIPE_DATA');

export const getCart = createAsyncThunk(
  CART_ALL_ACTION,
  async ({ storeId, user_pays_fees }, { rejectWithValue, getState }) => {
    try {
      const _storeId = storeId || getState().checkout.store.id;
      const { shippingMethod } = getState().checkoutForm.billingAddress;
      const { data, meta } = await GET_CART(_storeId, user_pays_fees);

      if (!data.length || !Array.isArray(data)) return { data: [] };
      const _meta = buildFromCartMeta(data, meta, shippingMethod);
      const items = data.map((item) => ({
        item_id: item.id,
        item_name: item.product.data.name,
        price: item.price,
        product_id: item.product_id,
        quantity: item.quantity,
      }));
      const ids = data.map((item) => item.id);
      logEvent('begin_checkout', {
        total: _meta.total,
        value: _meta.charge,
        currency: 'EUR',
        items,
        cart_product_ids: ids?.join(';') || '',
      });
      return {
        data: data.map((item) => flatCartProduct(item)),
        ..._meta,
      };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const updateCartProduct = createAsyncThunk(
  CART_UPDATE_ACTION,
  async (
    { cartProductId, payload, user_pays_fees, clearEditingProduct },
    { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const { data, meta } = await PATCH_CART_PRODUCT(
        cartProductId,
        payload,
        user_pays_fees
      );
      const item = data.find((_product) => _product.id === cartProductId);
      const { shippingMethod } = getState().checkoutForm.billingAddress;

      return {
        id: cartProductId,
        changes: flatCartProduct(item),
        ...buildFromCartMeta(data, meta, shippingMethod),
        clearEditingProduct,
      };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const deleteProduct = createAsyncThunk(
  CART_DELETE_ACTION,
  async ({ cartProduct }, { rejectWithValue, getState }) => {
    const item = {
      item_id: cartProduct.id,
      item_name: cartProduct.name,
      price: cartProduct.priceToDisplay,
      product_id: cartProduct.product_id,
      currency: 'EUR',
      quantity: cartProduct.quantity,
    };
    logEvent('remove_from_cart', {
      cart_product_id: cartProduct.id,
      product_id: cartProduct.product_id,
      amount: cartProduct.priceToDisplay,
      currency: 'EUR',
      value: cartProduct.priceToDisplay,
      items: [item],
    });
    try {
      const { meta, data } = await DELETE_CART_PRODUCT(cartProduct.id);
      const { shippingMethod } = getState().checkoutForm.billingAddress;
      return {
        id: cartProduct.id,
        ...buildFromCartMeta(data, meta, shippingMethod),
      };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const addProduct = createAsyncThunk(
  CART_ADD_ACTION,
  async (payload, { rejectWithValue, getState }) => {
    try {
      const currentCart = getState().cart.ids;
      const { shippingMethod } = getState().checkoutForm.billingAddress;
      const { data, meta } = await POST_CART(payload);
      const newItem = data.find(
        (_product) => currentCart.indexOf(_product.id) === -1
      );
      return {
        data: flatCartProduct(newItem),
        ...buildFromCartMeta(data, meta, shippingMethod),
      };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const cleanCart = createAsyncThunk(
  CART_CLEAN_ACTION,
  async (storeId, { rejectWithValue }) => {
    try {
      if (!storeId) return;
      return CLEAN_CART(storeId);
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const calculateFees = createAsyncThunk(
  CART_CALCULATE_FEES_ACTION,
  async (payload, { rejectWithValue, getState }) => {
    try {
      const companyId = getState().checkout.store.company_id;
      if (!companyId) return rejectWithValue('Company ID not found');
      const { id, ..._payload } = payload;
      const res = await POST_CALCULATE_FEES(companyId, _payload);

      return {
        id,
        changes: flatCalculateData({ total: res.total }),
        ...flatCalculateMeta(res),
      };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

const cartFulfilled = (state, { payload }) => {
  state.subtotal = payload.subtotal;
  state.discountApplied = payload.discountApplied;
  state.total = payload.total;
  state.shippingCost = payload.shippingCost;
  state.tax = payload.tax;
  state.userPaysFees = payload.userPaysFees;
  state.fees = payload.fees;
  state.charge = payload.charge;
  state.comments = payload.comments;
  state.isAnonymous = payload.isAnonymous;
};

const cartSlice = createSlice({
  name: CART,
  initialState,
  reducers: {
    clearCart: (state) => {
      return (state = initialState);
    },
    editCartProductTemp: (state, { payload }) => {
      state.editingProduct = payload;
    },
    updateIsAnonymous: (state, { payload }) => {
      state.isAnonymous = payload;
    },
    setShippingMethod: (state, { payload }) => {
      state.shippingMethod = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(calculateFees.pending, (state) => {
        state.calculatingFees = true;
      })
      .addCase(calculateFees.rejected, (state) => {
        state.calculatingFees = false;
      })
      .addCase(calculateFees.fulfilled, (state, { payload }) => {
        state.calculatingFees = false;
        cartAdapter.updateOne(state, payload);
        const containsNonFundraiserProducts = payload.data.some(isNonFundraiserProduct);
        state.containsNonFundraiserProducts = containsNonFundraiserProducts;
        state.charge = payload.charge;
        state.total = payload.total;
        state.fees = payload.fees;
      })
      .addCase(getCart.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCart.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getCart.fulfilled, (state, { payload }) => {
        state.loading = false;
        const containsNonFundraiserProducts = payload.data.some(isNonFundraiserProduct);
        state.containsNonFundraiserProducts = containsNonFundraiserProducts;
        state.currencySymbol = CURRENCY_SYMBOL[payload.currencySymbol];
        state.charge = payload.charge;
        cartAdapter.setAll(state, _compact(payload.data));
        if (!!payload.data.length) {
          cartFulfilled(state, { payload });
        }
      })
      .addCase(cleanCart.fulfilled, (state) => {
        return (state = initialState);
      })
      .addCase(updateCartProduct.pending, (state, { payload }) => {
        state.isUpdating = true;
      })
      .addCase(updateCartProduct.fulfilled, (state, { payload }) => {
        state.isUpdating = false;
        cartAdapter.updateOne(state, payload);
        if (payload.id) {
          cartFulfilled(state, { payload });
        }
        if (payload.clearEditingProduct) {
          state.editingProduct = null;
        }
      })
      .addCase(updateCartProduct.rejected, (state, { payload }) => {
        state.isUpdating = false;
      })
      .addCase(deleteProduct.fulfilled, (state, { payload }) => {
        cartAdapter.removeOne(state, payload.id);
        const remainingItems = Object.values(current(state.entities));
        if (remainingItems.length) {
          state.containsNonFundraiserProducts = remainingItems.some(isNonFundraiserProduct);
        }
        if (payload.id === state.editingProduct?.id) {
          state.editingProduct = null;
        }
        cartFulfilled(state, { payload });
      })
      .addCase(addProduct.fulfilled, (state, action) => {
        cartAdapter.addOne(state, action);
        const updatedItems = Object.values(current(state.entities));
        state.containsNonFundraiserProducts = updatedItems.some(isNonFundraiserProduct);
      })
      .addCase(setTotalCharge, (state, { payload }) => {
        state.charge = payload;
      })
      .addCase(setStripeTotalCharge, (state, { payload }) => {
        state.stripeTotal = payload;
      })
      .addCase(setStripeData, (state, { payload }) => {
        state.stripeData = payload;
      });
  },
});

export const cartSelector = cartAdapter.getSelectors((state) => state.cart);

export const cartProductsSelector = createDraftSafeSelector(
  [cartSelector.selectAll],
  (cart_product) => {
    return cart_product.map((item) => ({
      id: item.product_id,
      ...(item.product.productable_type === REGULAR_PRODUCT
        ? { quantity: item.quantity }
        : { amount: item.amount }),
    }));
  }
);
export const cartWithoutAmountSelector = createDraftSafeSelector(
  [cartSelector.selectAll],
  (cartProducts) => {
    return cartProducts.filter(
      (item) => item.type === FUNDRAISER_PRODUCT && item.amount === 0
    );
  }
);

export const {
  clearCart,
  editCartProductTemp,
  updateIsAnonymous,
  setShippingMethod,
} = cartSlice.actions;

export default cartSlice.reducer;
